<template>
  <div
    :class="[
      theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
      'rounded-lg border p-4 shadow-sm'
    ]"
  >
    <div class="flex items-center justify-between mb-4">
      <h3
        :class="[
          theme.isDark.value ? 'text-gray-200' : 'text-gray-900',
          'text-lg font-medium'
        ]"
      >
        Filters
      </h3>
      <button
        @click="clearFilters"
        :class="[
          theme.isDark.value
            ? 'text-gray-400 hover:text-gray-200'
            : 'text-gray-500 hover:text-gray-700',
          'text-sm font-medium'
        ]"
      >
        Clear All
      </button>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <!-- Resource Type Filter -->
      <div>
        <label
          for="resource-type"
          :class="[
            theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
            'block text-sm font-medium mb-1'
          ]"
        >
          Resource Type
        </label>
        <select
          id="resource-type"
          v-model="localFilters.resourceType"
          :class="[
            theme.isDark.value
              ? 'bg-gray-700 border-gray-600 text-gray-200'
              : 'bg-white border-gray-300 text-gray-700',
            'w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500'
          ]"
        >
          <option value="">All Types</option>
          <option
            v-for="type in resourceTypes"
            :key="type"
            :value="type"
          >
            {{ type }}
          </option>
        </select>
      </div>

      <!-- Resource ID Filter -->
      <div>
        <label
          for="resource-id"
          :class="[
            theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
            'block text-sm font-medium mb-1'
          ]"
        >
          Resource ID
        </label>
        <input
          id="resource-id"
          v-model="localFilters.resourceId"
          type="text"
          placeholder="Enter resource ID..."
          :class="[
            theme.isDark.value
              ? 'bg-gray-700 border-gray-600 text-gray-200'
              : 'bg-white border-gray-300 text-gray-700',
            'w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500'
          ]"
        />
      </div>

      <!-- User ID Filter -->
      <div>
        <label
          for="user-id"
          :class="[
            theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
            'block text-sm font-medium mb-1'
          ]"
        >
          User ID
        </label>
        <input
          id="user-id"
          v-model="localFilters.userId"
          type="text"
          placeholder="Enter user ID..."
          :class="[
            theme.isDark.value
              ? 'bg-gray-700 border-gray-600 text-gray-200'
              : 'bg-white border-gray-300 text-gray-700',
            'w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500'
          ]"
        />
      </div>

      <!-- Start Date Filter -->
      <div>
        <label
          for="start-date"
          :class="[
            theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
            'block text-sm font-medium mb-1'
          ]"
        >
          Start Date
        </label>
        <input
          id="start-date"
          v-model="localFilters.startDate"
          type="datetime-local"
          :class="[
            theme.isDark.value
              ? 'bg-gray-700 border-gray-600 text-gray-200'
              : 'bg-white border-gray-300 text-gray-700',
            'w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500'
          ]"
        />
      </div>

      <!-- End Date Filter -->
      <div>
        <label
          for="end-date"
          :class="[
            theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
            'block text-sm font-medium mb-1'
          ]"
        >
          End Date
        </label>
        <input
          id="end-date"
          v-model="localFilters.endDate"
          type="datetime-local"
          :class="[
            theme.isDark.value
              ? 'bg-gray-700 border-gray-600 text-gray-200'
              : 'bg-white border-gray-300 text-gray-700',
            'w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500'
          ]"
        />
      </div>

      <!-- HTTP Method Filter -->
      <div>
        <fieldset>
          <legend
            :class="[
              theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
              'block text-sm font-medium mb-1'
            ]"
          >
            HTTP Method
          </legend>
          <div class="space-y-2">
            <label
              v-for="method in httpMethods"
              :key="method"
              :class="[
                theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                'flex items-center space-x-2 text-sm'
              ]"
            >
              <input
                v-model="localFilters.httpMethod"
                type="checkbox"
                :value="method"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-blue-500'
                    : 'bg-white border-gray-300 text-blue-500',
                  'rounded focus:ring-blue-500'
                ]"
              />
              <span>{{ method }}</span>
            </label>
          </div>
        </fieldset>
      </div>
    </div>

    <!-- Apply Filters Button -->
    <div class="mt-4 flex justify-end">
      <button
        @click="applyFilters"
        :disabled="!hasChanges"
        :class="[
          hasChanges
            ? theme.isDark.value
              ? 'bg-blue-600 hover:bg-blue-700 text-white'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
            : theme.isDark.value
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          'px-4 py-2 rounded-md text-sm font-medium transition-colors'
        ]"
      >
        Apply Filters
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject } from 'vue'
import { useTheme } from '../../../utils/useTheme'
import type { AuditLogsFilters } from '../../../types/audit-logs'

// Use theme via inject with a fallback
const theme = inject('theme', useTheme())

interface Props {
  filters: AuditLogsFilters
  resourceTypes?: string[]
}

const props = withDefaults(defineProps<Props>(), {
  resourceTypes: () => []
})

const emit = defineEmits<{
  'update:filters': [filters: AuditLogsFilters]
  'apply-filters': [filters: AuditLogsFilters]
}>()

// Local filters state
const localFilters = ref<AuditLogsFilters>({ ...props.filters })

// Available HTTP methods
const httpMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH']

// Check if there are changes
const hasChanges = computed(() => {
  return JSON.stringify(localFilters.value) !== JSON.stringify(props.filters)
})

// Watch for external filter changes
watch(
  () => props.filters,
  (newFilters) => {
    localFilters.value = { ...newFilters }
  },
  { deep: true }
)

// Methods
const applyFilters = () => {
  emit('update:filters', { ...localFilters.value })
  emit('apply-filters', { ...localFilters.value })
}

const clearFilters = () => {
  localFilters.value = {
    resourceType: '',
    resourceId: '',
    userId: '',
    startDate: '',
    endDate: '',
    httpMethod: []
  }
  applyFilters()
}
</script>
